import { useEffect, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { useOrder } from "../../context/OrderContext";
import api from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Mail, MessageSquare, Lock, ShieldCheck, CreditCard } from "lucide-react";
import { formatPhone, digitsOnly, isValidUSPhone, toE164 } from "../../lib/phone";
import AddressAutocomplete from "../AddressAutocomplete";
import WaitlistCard from "../WaitlistCard";
import errorMessage from "../../lib/errors";

// stripePromise MUST live at module scope so loadStripe is called exactly once.
// Otherwise React re-creates the promise every render and Stripe emits a warning.
const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

const Field = ({ id, label, value, onChange, type = "text", required, placeholder, autoComplete }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider muted-text">{label}</label>
    <input
      data-testid={`checkout-${id}`}
      type={type} required={required} value={value}
      autoComplete={autoComplete}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="rounded-input mt-1"
    />
  </div>
);

export default function StepCheckout() {
  const { state, update, goBack } = useOrder();
  const [submitting, setSubmitting] = useState(false);
  const [serviceArea, setServiceArea] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const c = state.customer;
  // `setC` must always read the LATEST customer object — the Google Places
  // callback in AddressAutocomplete is only attached once (on first render)
  // and would otherwise close over an empty initial customer, wiping
  // name/email/phone the moment the user picks an autocomplete suggestion.
  const cRef = useRef(c);
  useEffect(() => { cRef.current = c; });
  const setC = (patch) => update({ customer: { ...cRef.current, ...patch } });

  // Lookup service area when ZIP changes (debounced via length check).
  useEffect(() => {
    const z = (c.zip_code || "").trim();
    if (z.length >= 5) {
      api.get(`/service-area?zip=${encodeURIComponent(z.slice(0, 5))}`)
        .then((r) => setServiceArea(r.data))
        .catch(() => setServiceArea(null));
    } else {
      setServiceArea(null);
    }
  }, [c.zip_code]);

  const phoneValid = isValidUSPhone(c.phone);
  const stateOk = (c.state || "").toUpperCase() === (serviceArea?.service_state || "CA");
  const smsConsent = !!c.sms_consent;
  const valid =
    c.name && c.email && phoneValid &&
    c.address_line1 && c.city && stateOk && c.zip_code &&
    smsConsent;

  const handlePhoneChange = (raw) => setC({ phone: digitsOnly(raw) });

  const handlePlaceSelected = ({ line1, city, state: stateCode, zip }) => {
    const cur = cRef.current;
    setC({
      address_line1: line1 || cur.address_line1,
      city: city || cur.city,
      state: stateCode || cur.state,
      zip_code: zip || cur.zip_code,
    });
  };

  // Build the embedded checkout session by first creating the order then
  // requesting a client_secret from the backend. Plain async fn — called
  // imperatively from startPayment so no useCallback wrapper needed (and a
  // useCallback with [] deps would freeze the closure with stale form values).
  const fetchClientSecret = async () => {
    const orderRes = await api.post("/orders", {
      product_slug: state.product.slug,
      quantity: state.quantity,
      petscreen_all: !!state.petscreenAll,
      mesh_type: state.meshType || "standard",
      white_glove: !!state.whiteGlove,
      customer: { ...c, phone: toE164(c.phone) },
      discount_code: state.discountCode || null,
    });
    const order = orderRes.data;
    update({
      orderId: order.id,
      orderNumber: order.order_number,
      measurementToken: order.measurement_token,
      measurementsRequired: order.measurements_required,
      items: Array.from({ length: state.quantity }, () => ({
        width: "", height: "", unit: "in",
        frame_color: "White", notes: "",
        photo_path: null, photo_url: null,
        petscreen: !!state.petscreenAll,
        spring_side: null,
        has_springs: true,
        has_pull_tabs: true,
      })),
    });
    const sessionRes = await api.post(`/orders/${order.id}/checkout-session`, {
      origin_url: window.location.origin,
    });
    return sessionRes.data.client_secret;
  };

  const startPayment = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      // Calling the same fetcher up-front lets us surface validation errors
      // (e.g. CA-only gating) BEFORE swapping the form out for the iframe.
      const secret = await fetchClientSecret();
      if (!secret) throw new Error("Could not start checkout");
      setClientSecret(secret);
    } catch (e) {
      toast.error(errorMessage(e, "Checkout failed"));
      setSubmitting(false);
    }
  };

  // Whether to show the waitlist card: state has been entered and is NOT CA.
  const stateEntered = (c.state || "").length === 2;
  const showWaitlist = stateEntered && !stateOk;

  // Once we have a clientSecret, swap the form out for the embedded checkout iframe.
  if (clientSecret && stripePromise) {
    return (
      <div data-testid="step-checkout-embedded">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2 text-[var(--brand-text)]">
          Payment
        </h1>
        <p className="muted-text mb-5">Enter your card. We'll email a receipt the moment it goes through.</p>

        <div className="rounded-2xl overflow-hidden border border-[var(--brand-border)]">
          <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>

        <div
          data-testid="trust-signals"
          className="mt-4 rounded-2xl border border-[var(--brand-border)] bg-[var(--brand-bg-soft,#f8faf6)] px-4 py-3"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div className="flex items-start gap-2">
              <Lock className="w-4 h-4 mt-0.5 shrink-0 brand-text" />
              <div>
                <p className="font-semibold text-[var(--brand-text)]">256-bit SSL</p>
                <p className="muted-text leading-snug">Bank-grade encryption end-to-end.</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 brand-text" />
              <div>
                <p className="font-semibold text-[var(--brand-text)]">Powered by Stripe</p>
                <p className="muted-text leading-snug">Same processor used by Amazon, Shopify, Lyft.</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <CreditCard className="w-4 h-4 mt-0.5 shrink-0 brand-text" />
              <div>
                <p className="font-semibold text-[var(--brand-text)]">Card data never touches us</p>
                <p className="muted-text leading-snug">PCI-compliant; we never see or store your card.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            data-testid="back-to-form-btn"
            type="button"
            onClick={() => { setClientSecret(null); setSubmitting(false); }}
            className="flex-1 py-3 rounded-full border-2 border-[var(--brand-border)] font-semibold"
          >
            Edit details
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="step-checkout">
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2 text-[var(--brand-text)]">
        Checkout
      </h1>
      <p className="muted-text mb-5">Secure your spot now — measurements come next.</p>

      <div className="rounded-2xl p-3 mb-4 border" style={{ background: "#fffbeb", borderColor: "#fde68a" }}>
        <p className="text-xs leading-snug text-amber-900">
          <strong>You'll get an email + text</strong> with your receipt and a link to enter measurements
          if you'd rather do it later.
        </p>
        <div className="flex gap-3 mt-2 text-xs text-amber-900">
          <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" /> Email</span>
          <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" /> SMS</span>
        </div>
      </div>

      <div className="option-card space-y-3">
        <Field id="name" label="Full name" autoComplete="name"
          value={c.name} onChange={(v) => setC({ name: v })} required placeholder="Jane Doe" />
        <Field id="email" label="Email" type="email" autoComplete="email"
          value={c.email} onChange={(v) => setC({ email: v })} required placeholder="jane@example.com" />

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider muted-text">Mobile phone</label>
          <input
            data-testid="checkout-phone"
            type="tel" inputMode="tel" autoComplete="tel-national" required
            value={formatPhone(c.phone)}
            onChange={(e) => handlePhoneChange(e.target.value)}
            placeholder="(555) 123-4567"
            className="rounded-input mt-1"
          />
          {c.phone && !phoneValid && (
            <p data-testid="phone-error" className="text-xs text-red-600 mt-1">
              Enter a valid 10-digit US phone number.
            </p>
          )}
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wider muted-text">Address line 1</label>
          <AddressAutocomplete
            value={c.address_line1}
            onChange={(v) => setC({ address_line1: v })}
            onPlace={handlePlaceSelected}
          />
        </div>

        <Field id="address2" label="Address line 2 (optional)" autoComplete="address-line2"
          value={c.address_line2} onChange={(v) => setC({ address_line2: v })} placeholder="Apt 4B" />
        <div className="grid grid-cols-3 gap-3">
          <Field id="city" label="City" autoComplete="address-level2"
            value={c.city} onChange={(v) => setC({ city: v })} required placeholder="Murrieta" />
          <Field id="state" label="State" autoComplete="address-level1"
            value={c.state} onChange={(v) => setC({ state: v.toUpperCase().slice(0, 2) })} required placeholder="CA" />
          <Field id="zip" label="ZIP" autoComplete="postal-code"
            value={c.zip_code} onChange={(v) => setC({ zip_code: v })} required placeholder="92562" />
        </div>

        {stateOk && (
          <p data-testid="ca-tax-note" className="text-xs muted-text">
            CA sales tax (8.75%) will be added on the product subtotal.
          </p>
        )}

        {stateOk && serviceArea && c.zip_code && (
          <p
            data-testid={serviceArea.in_zone ? "zip-in-zone" : "zip-out-of-zone"}
            className="text-xs"
            style={{ color: serviceArea.in_zone ? "var(--brand-green)" : "#92400e" }}
          >
            {serviceArea.in_zone
              ? "Delivery & install available for your ZIP."
              : "Outside our delivery zone — pickup at the shop is free."}
          </p>
        )}
      </div>

      {showWaitlist && (
        <WaitlistCard email={c.email} state={c.state} zip={c.zip_code} />
      )}

      {/* --- SMS consent (Twilio toll-free compliance) ---
          Unchecked by default. Required to submit the order. The backend
          stamps `sms_consent_at` server-side when this is true. */}
      <label
        data-testid="sms-consent-card"
        htmlFor="sms-consent-checkbox"
        className={`mt-4 flex gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-colors ${
          smsConsent
            ? "border-[var(--brand-green)] bg-[var(--brand-green-light)]/30"
            : "border-[var(--brand-border)] bg-white"
        }`}
      >
        <input
          id="sms-consent-checkbox"
          data-testid="sms-consent-checkbox"
          type="checkbox"
          checked={smsConsent}
          onChange={(e) => setC({ sms_consent: e.target.checked })}
          className="mt-1 w-5 h-5 shrink-0 accent-[var(--brand-green)] cursor-pointer"
        />
        <span className="text-xs leading-relaxed text-[var(--brand-text)]">
          I agree to receive text messages from ScreenzZ regarding my order,
          scheduling, delivery, measurement appointments, and customer support.
          Message &amp; data rates may apply. Reply <strong>STOP</strong> to opt out.
          {" "}
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="brand-text font-semibold underline underline-offset-2"
            data-testid="sms-consent-privacy-link"
            onClick={(e) => e.stopPropagation()}
          >
            Privacy Policy
          </a>
          {" · "}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="brand-text font-semibold underline underline-offset-2"
            data-testid="sms-consent-terms-link"
            onClick={(e) => e.stopPropagation()}
          >
            Terms &amp; Conditions
          </a>
          .
        </span>
      </label>

      <div className="flex gap-3 mt-8">
        <button data-testid="back-btn" type="button" onClick={goBack} disabled={submitting}
          className="flex-1 py-3 rounded-full border-2 border-[var(--brand-border)] font-semibold">
          Back
        </button>
        <button data-testid="place-order-btn" className="cta-btn flex-1" disabled={!valid || submitting} onClick={startPayment}>
          {submitting
            ? <span className="inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading payment…</span>
            : "Continue to payment"}
        </button>
      </div>
    </div>
  );
}
